import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronDown, ChevronUp, Telescope } from "lucide-react";
import { publicDiscoveries } from "../lib/adminApi";
import BottleSilhouette from "./BottleSilhouette";
import GinImage from "./GinImage";

const CATEGORY_LABEL = {
  botanical: "Botanical discovery",
  production: "Production note",
  cultural: "From the story",
  bottle: "Bottle trivia",
  service: "How to serve",
  recognition: "Recognition",
  beno: "From the BENO bar",
};

const CONFIDENCE_TONE = {
  VERIFIED: "text-green-400 border-green-700/40",
  STRONG: "text-[#C17F59] border-[#C17F59]/40",
  PARTIAL: "text-amber-400 border-amber-600/40",
  NEEDS_REVIEW: "text-red-400 border-red-600/40",
};

function freshnessLabel(featured_at) {
  try {
    const d = new Date(featured_at);
    const days = Math.round((Date.now() - d.getTime()) / 86400000);
    if (days <= 0) return "Today";
    if (days === 1) return "Yesterday";
    if (days <= 7) return "This week";
    if (days <= 14) return "Last 2 weeks";
    return `${days}d ago`;
  } catch { return ""; }
}

export default function DiscoveriesStrip() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    publicDiscoveries(8).then((d) => { setItems(d.discoveries || []); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading || items.length === 0) return null;

  return (
    <section className="px-5 md:px-12 max-w-7xl mx-auto pt-16 pb-8 border-t border-white/5" data-testid="discoveries-strip">
      <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <p className="overline text-[#C17F59] mb-2 flex items-center gap-2"><Telescope size={12} /> Fresh from the Atlas</p>
          <h2 className="font-display text-3xl md:text-4xl tracking-tight leading-tight">Newly discovered.</h2>
          <p className="font-hand text-lg text-[#D89A74] -rotate-1 mt-1">— notes from the bar this week.</p>
        </div>
        <p className="text-[10px] uppercase tracking-[0.22em] text-white/40">{items.length} discoveries · approved by BENO</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="discoveries-grid">
        {items.map((d, i) => <DiscoveryCard key={d.id} d={d} i={i} />)}
      </div>
    </section>
  );
}

function DiscoveryCard({ d, i }) {
  const [open, setOpen] = useState(false);
  const tone = CONFIDENCE_TONE[d.confidence_state] || "text-white/50 border-white/15";
  return (
    <motion.article
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: Math.min(i * 0.06, 0.4), ease: [0.16, 1, 0.3, 1] }}
      className="bg-[#15171A] border border-white/8 hover:border-[#C17F59]/40 p-5 transition-colors flex flex-col"
      data-testid={`discovery-${d.id}`}
    >
      <header className="flex items-start gap-4 mb-3">
        <div className="shrink-0 -mt-1">
          <GinImage gin={d.gin} size={42} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="overline text-[#C17F59] truncate">{CATEGORY_LABEL[d.category] || d.category}</p>
          <Link to={`/gin/${d.gin.id}`} className="font-display text-xl leading-tight hover:text-[#C17F59] transition-colors block truncate">
            {d.gin.name}
          </Link>
          <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 mt-0.5">{d.gin.country}</p>
        </div>
      </header>

      <blockquote className="font-display text-lg leading-snug text-white/85 italic mb-3 flex-1">
        "{d.text}"
      </blockquote>

      {d.why_this_matters && (
        <button
          onClick={() => setOpen((o) => !o)}
          className="text-[10px] uppercase tracking-[0.22em] text-white/50 hover:text-[#C17F59] inline-flex items-center gap-1 mb-2 self-start"
          data-testid={`discovery-why-${d.id}`}
        >
          Why this matters {open ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
        </button>
      )}
      {open && d.why_this_matters && (
        <p className="text-xs text-white/65 italic border-l-2 border-[#C17F59]/30 pl-3 mb-3 leading-relaxed">
          {d.why_this_matters}
        </p>
      )}

      <footer className="flex items-center justify-between pt-2 border-t border-white/5 mt-auto">
        <span className={`inline-block text-[9px] uppercase tracking-[0.22em] border px-2 py-0.5 ${tone}`}>
          {d.confidence_state} · BENO
        </span>
        <span className="text-[10px] uppercase tracking-[0.22em] text-white/40">
          {freshnessLabel(d.featured_at)}
        </span>
      </footer>
    </motion.article>
  );
}
